import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { BaseLoadingStore } from "app/modules/common/stores/BaseLoadingStore";
import { inject } from "react-ioc";
import { ApiClients } from "app/modules/main/services/AxiosBaseClient";
import {
    HostToolsListingSummary,
    HostToolsPropertyOption,
    IntegrationModel
} from "app/modules/main/api/admin-clients.api";
import { AdminMenu, AdminStore } from "app/modules/main/components/admin/store/AdminStore";

export class ListingsStore extends BaseLoadingStore {
    @inject apiClients: ApiClients;
    @inject adminStore: AdminStore;

    integrations: IntegrationModel[] = [];
    selectedIntegrationId: number = null;
    listings: HostToolsListingSummary[] = [];
    propertyOptions: HostToolsPropertyOption[] = [];

    constructor() {
        super();

        makeObservable(this, {
            integrations: observable,
            selectedIntegrationId: observable,
            listings: observable,
            propertyOptions: observable,

            hasHosttoolsIntegrations: computed,

            setSelectedIntegration: action,
            setListings: action,
            setPropertyOptions: action,
        });
    }

    get hasHosttoolsIntegrations(): boolean {
        return this.integrations?.some(x => x.type === 2 /* IntegrationType.HosttoolsApi */) ?? false;
    }

    init = async () => {
        await this.wrapInitLoadingApiCall(async () => {
            this.adminStore.selectMenu(AdminMenu.IntegrationListings);

            const integrations = await this.apiClients.adminPropertyClient.getIntegrations();
            const hosttools = integrations.filter(x => x.type === 2);

            runInAction(() => {
                this.integrations = hosttools;
            });

            if (hosttools.length === 0) {
                return;
            }

            this.setSelectedIntegration(hosttools[0].id);
            await this.loadListingsInternal();
        });
    };

    setSelectedIntegration = (integrationId: number) => {
        this.selectedIntegrationId = integrationId;
    };

    setListings = (items: HostToolsListingSummary[]) => {
        this.listings = items ?? [];
    };

    setPropertyOptions = (items: HostToolsPropertyOption[]) => {
        this.propertyOptions = items ?? [];
    };

    loadListings = async () => {
        await this.wrapLoadingApiCall(() => this.loadListingsInternal());
    };

    private loadListingsInternal = async () => {
        if (this.selectedIntegrationId == null) {
            this.setListings([]);
            this.setPropertyOptions([]);
            return;
        }

        const [listings, propertyOptions] = await Promise.all([
            this.apiClients.integrationClient.getHosttoolsListings(this.selectedIntegrationId),
            this.apiClients.integrationClient.getHosttoolsListingPropertyOptions(this.selectedIntegrationId),
        ]);

        runInAction(() => {
            this.setListings(listings);
            this.setPropertyOptions(propertyOptions);
        });
    };

    refreshPropertyOptions = async () => {
        if (this.selectedIntegrationId == null) {
            this.setPropertyOptions([]);
            return;
        }

        const options = await this.apiClients.integrationClient.getHosttoolsListingPropertyOptions(this.selectedIntegrationId);
        runInAction(() => this.setPropertyOptions(options));
    };

    linkListingToProperty = async (listingSyncId: number, propertyId: number) => {
        await this.wrapLoadingApiCall(async () => {
            await this.apiClients.integrationClient.linkHosttoolsListing(listingSyncId, propertyId);
            await this.loadListingsInternal();
        });
    };

    createPropertyForListing = async (listingSyncId: number) => {
        await this.wrapLoadingApiCall(async () => {
            await this.apiClients.integrationClient.createPropertyForHosttoolsListing(listingSyncId);
            await this.loadListingsInternal();
        });
    };

    setIgnore = async (listingSyncId: number, ignore: boolean) => {
        await this.wrapLoadingApiCall(async () => {
            await this.apiClients.integrationClient.setHosttoolsListingIgnore(listingSyncId, ignore);
            await this.loadListingsInternal();
        });
    };
}

